import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';

import menuItems from 'menu-items';
import { useAccessControl } from 'contexts/AccessControlContext';
import { RAIL_WIDTH, PANEL_WIDTH, HEADER_HEIGHT } from '../index';
import metallicPalette from 'themes/colors';

const MODULE_ACCESS_PATHS = {
  'virtual-meetings': ['/meetings'],
  meetings: ['/meetings'],
  employee: ['/employees'],
  attendance: ['/attendance'],
  leaves: ['/leave'],
  payroll: ['/payroll'],
  expenses: ['/payroll', '/expenses'],
  projects: ['/projects'],
  assets: ['/assets'],
  performance: ['/performance'],
  recruitment: ['/recruitment'],
  'user-management': ['/user-management', '/user', '/role', '/menu', '/role-mapping', '/approval-workflows'],
  'system-config': [
    '/system-config',
    '/system-config/company',
    '/system-config/company-branch',
    '/system-config/department',
    '/system-config/designation',
    '/company',
    '/company-branch',
    '/department',
    '/designation',
    '/masters'
  ]
};

const CHILD_ACCESS_PATHS = {
  'sc-company': ['/system-config/company', '/company', '/masters', '/system-config'],
  'sc-branch': ['/system-config/company-branch', '/company-branch', '/company', '/masters', '/system-config'],
  'sc-department': ['/system-config/department', '/department', '/masters', '/system-config'],
  'sc-designation': ['/system-config/designation', '/designation', '/masters', '/system-config'],
  'payroll-dashboard': ['/payroll/dashboard'],
  'client-staff-payroll': ['/payroll/client-staff', '/payroll/employee-payroll'],
  'payroll-run': ['/payroll/run'],
  'payroll-salary': ['/payroll/salary-structure'],
  'payroll-employee-payroll': ['/payroll/employee-payroll'],
  'payroll-expense-claims': ['/expenses/claims'],
  'payroll-expense-approvals': ['/expenses/approvals', '/payroll/run'],
  'payroll-company-expenses': ['/expenses/company-expenses'],
  'payroll-payslips': ['/payroll/payslips'],
  'payroll-reports': ['/payroll/reports'],
  'payroll-settings': ['/payroll/settings'],
  'expense-claims': ['/expenses/claims'],
  'expense-approvals': ['/expenses/approvals'],
  'company-expenses': ['/expenses/company-expenses'],
  'project-dashboard': ['/projects/dashboard'],
  'project-list': ['/projects/list'],
  'project-clients': ['/projects/clients'],
  'project-tasks': ['/projects/tasks'],
  'project-milestones': ['/projects/milestones'],
  'project-reviews': ['/projects/reviews'],
  'project-resources': ['/projects/resources'],
  'project-risks': ['/projects/risks'],
  'project-timesheets': ['/projects/timesheets'],
  'project-reports': ['/projects/reports'],
  'project-analytics': ['/projects/analytics'],
  'project-settings': ['/projects/settings'],
  'um-users': ['/user'],
  'um-roles': ['/role'],
  'um-permissions': ['/menu', '/role-mapping'],
  'um-workflows': ['/approval-workflows'],
  'att-wfh': ['/attendance/regularization'],
  'assets-dashboard': ['/assets/dashboard', '/assets'],
  'assets-list': ['/assets/list', '/assets'],
  'assets-assignments': ['/assets/assignments', '/assets'],
  'assets-requests': ['/assets/requests', '/assets'],
  'assets-stock': ['/assets/stock', '/assets'],
  'assets-depreciation': ['/assets/depreciation', '/assets'],
  'assets-maintenance': ['/assets/maintenance', '/assets'],
  'assets-procurement': ['/assets/procurement', '/assets/purchases', '/assets/vendors', '/assets'],
  'assets-reports': ['/assets/reports', '/assets']
};

function getLightSidebarColors() {
  const primaryMain = metallicPalette.primary.main;
  const primaryLight = metallicPalette.primary.light || primaryMain;
  const primaryLighter = metallicPalette.primary.lighter || primaryLight;

  return {
    railBg: `linear-gradient(180deg, ${metallicPalette.grey[0]} 0%, ${primaryLighter} 50%, ${alpha(metallicPalette.primary[200] || primaryLight, 0.45)} 100%)`,
    panelBg: `linear-gradient(180deg, #FFFFFF 0%, ${metallicPalette.grey[1]} 60%, ${primaryLighter} 100%)`,
    activeColor: primaryMain,
    activeDarker: metallicPalette.primary.darker,
    inactiveColor: metallicPalette.grey[6],
    textColor: metallicPalette.grey[8],
    border: alpha(primaryMain, 0.12),
    headerBg: alpha(primaryMain, 0.05),
    divider: alpha(primaryMain, 0.15)
  };
}

// Dark mode colors
const D = {
  railBg: 'linear-gradient(180deg, #1E293B 0%, #162032 50%, #0F172A 100%)',
  panelBg: 'linear-gradient(180deg, #1E293B 0%, #162032 60%, #0F172A 100%)',
  activeColor: '#60A5FA',
  activeDarker: '#93C5FD',
  inactiveColor: '#94A3B8',
  textColor: '#CBD5E1',
  border: alpha('#60A5FA', 0.12),
  headerBg: alpha('#60A5FA', 0.06),
  divider: '#334155'
};

function useIsDark() {
  const [dark, setDark] = useState(() => localStorage.getItem('hrmsThemeMode') === 'dark');
  useEffect(() => {
    const handler = () => setDark(localStorage.getItem('hrmsThemeMode') === 'dark');
    window.addEventListener('hrmsThemeChanged', handler);
    return () => window.removeEventListener('hrmsThemeChanged', handler);
  }, []);
  return dark;
}

function isItemActive(item, pathname) {
  if (item.url && matchPath({ path: item.url, end: true }, pathname)) return true;
  if (item.children) return item.children.some((c) => isItemActive(c, pathname));
  return false;
}

function getActiveModuleId(pathname, items) {
  for (const group of items) {
    for (const mod of group.children || []) {
      if (isItemActive(mod, pathname)) return mod.id;
    }
  }
  return null;
}

function RailItem({ mod, isActive, isSelected, onClick, onPointerEnter, c }) {
  const Icon = mod.icon;

  const inner = (
    <Box
      onClick={() => onClick(mod)}
      onPointerEnter={() => onPointerEnter?.(mod)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.6,
        width: RAIL_WIDTH - 10,
        minHeight: 56,
        borderRadius: '10px',
        cursor: 'pointer',
        mx: 'auto',
        px: 0.5,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        color: isActive || isSelected ? c.activeDarker : c.inactiveColor,
        bgcolor: isActive ? alpha(c.activeColor, 0.16) : isSelected ? alpha(c.activeColor, 0.08) : 'transparent',
        boxShadow: isActive ? `inset 3.5px 0 0 ${c.activeColor}, 0 2px 8px ${alpha(c.activeColor, 0.12)}` : 'none',
        '&:hover': {
          bgcolor: alpha(c.activeColor, 0.1),
          color: c.activeDarker,
          transform: 'translateY(-1px)'
        }
      }}
    >
      {Icon && <Icon style={{ fontSize: 19, color: isActive || isSelected ? c.activeColor : 'inherit' }} />}
      <Typography
        sx={{
          fontSize: '0.6rem',
          fontWeight: isActive || isSelected ? 700 : 600,
          lineHeight: 1.15,
          textAlign: 'center',
          maxWidth: RAIL_WIDTH - 9,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'wrap',
          color: 'inherit'
        }}
      >
        {mod.title}
      </Typography>
    </Box>
  );

  if (mod.type === 'item') {
    return (
      <Tooltip title={mod.title} placement="right" arrow>
        <Box component={Link} to={mod.url} sx={{ textDecoration: 'none', display: 'block', px: 0.5, py: 0.3 }}>
          {inner}
        </Box>
      </Tooltip>
    );
  }

  return <Box sx={{ px: 0.5, py: 0.3 }}>{inner}</Box>;
}

function SubPanel({ mod, c }) {
  const { pathname } = useLocation();

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        height: '100%',
        background: c.panelBg,
        borderRight: `1px solid ${c.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          borderBottom: `1px solid ${c.border}`,
          bgcolor: c.headerBg,
          flexShrink: 0
        }}
      >
        <Typography sx={{ fontWeight: 800, color: c.activeDarker, fontSize: '0.82rem', letterSpacing: 0.3 }}>{mod.title}</Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {mod.children?.map((child) => {
          const active = !!matchPath({ path: child.url, end: false }, pathname);
          return (
            <Box
              key={child.id}
              component={Link}
              to={child.url}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2,
                py: 0.85,
                mx: 0.75,
                borderRadius: '0 8px 8px 0',
                borderLeft: active ? `3px solid ${c.activeColor}` : '3px solid transparent',
                textDecoration: 'none',
                transition: 'all 0.15s ease',
                color: active ? c.activeDarker : c.textColor,
                bgcolor: active ? alpha(c.activeColor, 0.12) : 'transparent',
                '&:hover': {
                  bgcolor: alpha(c.activeColor, 0.08),
                  color: c.activeDarker
                }
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: active ? c.activeColor : alpha(c.inactiveColor, 0.4)
                }}
              />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: active ? 700 : 500, color: 'inherit', lineHeight: 1.4 }}>
                {child.title}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const dark = useIsDark();
  const [, setThemeTick] = useState(0);
  const c = dark ? D : getLightSidebarColors();

  useEffect(() => {
    const handler = () => setThemeTick((tick) => tick + 1);
    window.addEventListener('hrmsThemeChanged', handler);
    return () => window.removeEventListener('hrmsThemeChanged', handler);
  }, []);

  const { menus } = useAccessControl();
  const allowedPaths = useMemo(() => (menus === null ? null : extractAllowedPaths(menus)), [menus]);
  const visibleMenuItems = useMemo(
    () => (menus === null ? { items: [] } : filterMenuItems(menuItems, allowedPaths)),
    [allowedPaths, menus]
  );
  const currentActiveId = getActiveModuleId(pathname, visibleMenuItems.items);
  const [selectedId, setSelectedId] = useState(() => currentActiveId);
  const [panelOpen, setPanelOpen] = useState(false);
  const insideRef = useRef(false);

  useEffect(() => {
    if (currentActiveId) setSelectedId(currentActiveId);
  }, [pathname, currentActiveId]);

  const allMods = visibleMenuItems.items.flatMap((g) => g.children || []);
  const selectedMod = allMods.find((m) => m.id === selectedId) || null;
  const showPanel = panelOpen && selectedMod?.type === 'collapse';

  function handleSidebarPointerEnter() {
    insideRef.current = true;
  }
  function handleSidebarPointerLeave() {
    insideRef.current = false;
    setPanelOpen(false);
  }
  function handleRailPointerEnter(mod) {
    if (mod.type === 'collapse') {
      setSelectedId(mod.id);
      setPanelOpen(true);
    } else setPanelOpen(false);
  }
  function handleRailClick(mod) {
    if (mod.type === 'item') {
      setSelectedId(mod.id);
      setPanelOpen(false);
    } else {
      setSelectedId(mod.id);
      setPanelOpen(true);
    }
  }

  const sidebarContent = (
    <Box
      onPointerEnter={handleSidebarPointerEnter}
      onPointerLeave={handleSidebarPointerLeave}
      sx={{
        display: 'flex',
        height: '100%',
        bgcolor: 'transparent',
        borderRight: `1px solid ${c.border}`
      }}
    >
      {/* Icon Rail */}
      <Box
        sx={{
          width: RAIL_WIDTH,
          flexShrink: 0,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          background: c.railBg,
          borderRight: showPanel ? `1px solid ${c.border}` : 'none',
          display: 'flex',
          flexDirection: 'column',
          pt: 1,
          pb: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {visibleMenuItems.items.map((group, gi) => (
          <Box key={group.id}>
            {group.children?.map((mod) => (
              <RailItem
                key={mod.id}
                mod={mod}
                isActive={isItemActive(mod, pathname)}
                isSelected={selectedId === mod.id}
                onClick={handleRailClick}
                onPointerEnter={handleRailPointerEnter}
                c={c}
              />
            ))}
            {gi < visibleMenuItems.items.length - 1 && <Divider sx={{ my: 0.75, mx: 1.5, opacity: 0.35, borderColor: c.divider }} />}
          </Box>
        ))}
      </Box>

      {/* Sub Panel */}
      {showPanel && <SubPanel mod={selectedMod} c={c} />}
    </Box>
  );

  if (downLG) {
    return (
      <Drawer
        variant="temporary"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            top: HEADER_HEIGHT,
            height: `calc(100% - ${HEADER_HEIGHT}px)`,
            width: RAIL_WIDTH + (showPanel ? PANEL_WIDTH : 0),
            boxSizing: 'border-box',
            border: 'none'
          }
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: RAIL_WIDTH + (showPanel ? PANEL_WIDTH : 0),
        transition: 'width 0.22s ease',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      {sidebarContent}
    </Box>
  );
}

function extractAllowedPaths(items = []) {
  const paths = new Set();
  function walk(nodes) {
    nodes.forEach((node) => {
      const routerLink = Array.isArray(node.routerLink) ? node.routerLink[0] : node.routerLink;
      const actions = node.actions || {};
      const hasDirectAccess = Object.values(actions).some(Boolean);
      if (routerLink && hasDirectAccess) paths.add(normalizePath(routerLink));
      if (Array.isArray(node.items)) walk(node.items);
    });
  }
  walk(Array.isArray(items) ? items : []);
  return paths;
}

function filterMenuItems(source, allowedPaths) {
  if (!allowedPaths) return source;
  return {
    items: source.items
      .map((group) => ({
        ...group,
        children: (group.children || []).map((item) => filterMenuItem(item, allowedPaths)).filter(Boolean)
      }))
      .filter((group) => group.children.length > 0)
  };
}

function filterMenuItem(item, allowedPaths) {
  if (item.id === 'virtual-meetings' || item.id === 'meetings') return item;
  if (item.id === 'dashboard') return hasAnyPath(allowedPaths, itemAccessPaths(item)) ? item : null;
  if (item.id === 'settings') return item;
  if (item.children) {
    const children = item.children.map((child) => filterMenuItem(child, allowedPaths)).filter(Boolean);
    return children.length ? { ...item, children } : null;
  }
  return hasAnyPath(allowedPaths, itemAccessPaths(item)) ? item : null;
}

function hasAnyPath(allowedPaths, paths) {
  return paths.filter(Boolean).some((path) => allowedPaths.has(normalizePath(path)));
}

function normalizePath(path) {
  const value = String(path || '').trim();
  if (!value) return '';
  return value.startsWith('/') ? value : `/${value}`;
}

function itemAccessPaths(item) {
  return [...(MODULE_ACCESS_PATHS[item.id] || []), ...(CHILD_ACCESS_PATHS[item.id] || []), normalizePath(item.url)].filter(Boolean);
}
